<template>
    <div>
        <h2>{{ reportLabel }}</h2>

        <div v-loading="loadingAll !== false" element-loading-text="Loading...">

            <!-- FILTERS -->
            <div v-if="activeFilters.length !== 0" style="display:flex;flex-wrap:wrap;gap:10px;">
                <template v-for="(filterData, filterKey) in activeFilters">
                    <component :is="filterData.component"
                               :values="filterData.values"
                               :key="`filter-${filterKey}`"
                               :label="filterData.label"
                               :id="filterData.id"
                               :report-id="reportId"
                               @changed="(newValues) => updateFilterValues(filterKey, newValues)"
                    ></component>
                </template>
                <div style="padding-top:25px;">
                    <btn cssClass="btn-secondary" icon="fa-fw fas fa-redo-alt" :click="saveFilters">
                        Save Filters & Refresh
                    </btn>
                </div>
            </div>
            <div v-else style="padding-top:10px;">
                <btn cssClass="btn-secondary" icon="fa-fw fas fa-redo-alt" :click="refresh">
                    Refresh All Data
                </btn>
            </div>

            <!-- RENDERERS -->
            <div v-if="tables.length > 0">
                <el-card v-for="(tableData, tableKey) in tables" :key="`table-${tableKey}`"
                         class="box-card"
                         style="margin-top:20px;">
                    <div slot="header" class="clearfix">
                        <span style="font-weight:bold;">{{ tableData['label'] }}</span>
                        <div v-if="loadingTables !== false || tablesData[tableData['id']].processing !== false" style="float:right;display:inline-block;">
                            <span style="padding-right:5px;font-size:1.6rem;"><i class="far fa-spinner fa-pulse"></i></span>
                            <b style="font-weight:bold;">Refreshing Data.</b> Depending on the complexity of the report, refresh may take a few minutes.
                        </div>
                        <div v-else-if="(tablesData[tableData['id']].renderer || null) === 'renderer-table'" style="float:right;display:inline-block;">
                            <btn cssClass="btn-link" style="padding:0;" icon="fa-fw fas fa-download" :click="() => exportTableToCSV(tableData['id'])">
                                Export CSV
                            </btn>
                        </div>
                    </div>
                    <div style="min-height:50px;max-height:500px;overflow:scroll;">
                        <component
                            v-if="tablesData[tableData['id']] !== undefined && tablesData[tableData['id']] !== null"
                            :is="tablesData[tableData['id']].renderer"
                            :id="tableData['id']"
                            :data="tablesData[tableData['id']].data"
                            :is-processing="tablesData[tableData['id']].processing"
                            :chart-data="tablesData[tableData['id']].chart || null"
                            :formatters="tablesData[tableData['id']].formatters"></component>
                    </div>
                </el-card>
            </div>
            <div v-else style="margin-top:50px;">
                <p style="text-align:center;"><b>Report has no tables.</b><br>This is probably a bug, please contact DEV team.</p>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    components: {
        // filters
        'filter-date': () => import('./Filters/filter-date.vue'),
        'filter-date-range': () => import('./Filters/filter-date-range.vue'),
        'filter-multi-select': () => import('./Filters/filter-multi-select.vue'),
        'filter-multi-select-id-visible': () => import('./Filters/filter-multi-select-id-visible.vue'),
        'filter-single-select': () => import('./Filters/filter-single-select.vue'),
        'filter-single-select-id-visible': () => import('./Filters/filter-single-select-id-visible.vue'),
        // data outputs
        'renderer-table': () => import('./Renderers/renderer-table.vue'),
        'renderer-chart': () => import('./Renderers/renderer-chart.vue'),
        'renderer-pivot': () => import('./Renderers/renderer-pivot.vue'),
    },
    props: [
        'reportLabel',
        'reportId',
        'filters',
        'tables',
    ],
    data() {
        return {
            loadingAll: true,
            loadingTables: true,
            activeFilters: this.filters,
            tablesData: {},
            userId: window.user_id,
            pusher: {
                all_users: null,
            },
        };
    },
    created() {
        this.pusher.all_users = Echo.private(`jb-all-users`);
        this.pusher.all_users.listen('.report-view-cached', (event) => {
            if (event.userId === this.userId && event.reportId === this.reportId) {
                this.loadTables();
            }
        });
    },
    beforeDestroy() {
        this.pusher.all_users.stopListening('.report-view-cached');
    },
    mounted() {
        this.loadingAll = true;

        this.loadTables().finally(() => {
            this.loadingAll = false;
        });
    },
    methods: {
        loadTables() {
            if (this.tables.length === 0) {
                this.loadingTables = false;
                return;
            }

            this.loadingTables = true;

            return Promise.all(this.tables.map(table => {
                return axios.post(url(`admin/custom-reports/${this.reportId}/get-table-data`), {
                    table_name: table['id']
                }).then(response => {
                    Vue.set(this.tablesData, table['id'], response.data);
                }).catch(error => {
                    this.$message.error(error.message);
                });
            })).finally(() => {
                this.loadingTables = false;
            });
        },
        updateFilterValues(filterId, filterValues) {
            Vue.set(this.activeFilters[filterId], 'values', filterValues);
        },
        saveFilters() {
            this.loadingAll = true;

            axios.post(url(`admin/custom-reports/${this.reportId}/save-filters`), {
                'filters': this.activeFilters,
            }).then(response => {
                if (response.data.filters || false) {
                    this.activeFilters = response.data.filters;
                }

                if (response.data.filters_changed || true) {
                    this.loadingTables = true;
                }
                this.loadingAll = false;
            });
        },
        refresh() {
            this.loadingAll = true;

            axios.post(url(`admin/custom-reports/${this.reportId}/refresh`)).then(response => {
                if (response.data.refresh_performed || false) {
                    this.loadingTables = true;
                }
                this.loadingAll = false;
            });
        },
        exportTableToCSV(tableId) {
            let csvData = [];

            let headers = [];
            let headerRow = [];
            for (const property in this.tablesData[tableId].data.columns) {
                headers.push(property);
                headerRow.push(this.tablesData[tableId].data.columns[property]);
            }
            csvData.push(headerRow);

            this.tablesData[tableId].data.rows.forEach(row => {
                let rowValues = [];
                headers.forEach(header => {
                    let val = row[header].toString();
                    if (val.indexOf(',') !== -1) {
                        val = `"${val}"`;
                    }
                    rowValues.push(val);
                });
                csvData.push(rowValues);
            });

            const url = window.URL.createObjectURL(new Blob([csvData.join('\r\n')], {type: 'text/csv'}));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'report_export.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    }
}
</script>

<style scoped>
h2 {
    text-transform: uppercase;
}
</style>